import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Avatar,
  Typography,
  Tag,
  Tooltip,
  Space,
  Row,
  Col,
  Card,
  theme,
} from "antd";
import { PlusOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Employee, ScheduleType, Day } from "../../../types/schedule";
import { getOptimalTextColor } from "../../../Util/colorUtils";
import ScheduleDetailsDrawer from "./ScheduleDetailsDrawer";

const { Text, Title } = Typography;

interface EmployeeRecord {
  key: string;
  employee: Employee;
}

interface TwoWeekViewGridProps {
  employees: Employee[];
  scheduleData: ScheduleType[];
  currentWeek: Date;
  onAddEvent: (event: Record<string, string | number | boolean>) => void;
  onRefresh?: () => void;
  loading?: boolean;
}

// Your custom category colors
const categoryColors: Record<string, string> = {
  scheduled: "green",
  timeoffpending: "orange",
  timeoff: "red",
  absent: "volcano",
  meeting: "blue",
  other: "default",
};

const TwoWeekViewGrid: React.FC<TwoWeekViewGridProps> = ({
  employees,
  scheduleData,
  currentWeek,
  onAddEvent,
  onRefresh,
  loading = false,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null,
  );
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const { token } = theme.useToken();

  // Monitor screen width for responsive behavior
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate responsive column width - smaller for two-week view
  const getColumnWidth = () => {
    if (screenWidth < 768) return 80; // Very compact for mobile
    if (screenWidth < 1024) return 90; // Compact for tablet
    if (screenWidth < 1280) return 100; // Small desktop
    return 120; // Large desktop (smaller than single week)
  };

  // Calculate employee column width
  const getEmployeeColumnWidth = () => {
    if (screenWidth < 768) return 120; // Mobile
    if (screenWidth < 1024) return 140; // Tablet
    return 160; // Desktop (smaller than single week)
  };

  // Generate all 7 days for both weeks (Sunday to Saturday)
  const currentWeekDays = Array.from({ length: 7 }, (_, i) =>
    dayjs(currentWeek).add(i, "day").toDate(),
  );
  const nextWeek = dayjs(currentWeek).add(1, "week").toDate();
  const nextWeekDays = Array.from({ length: 7 }, (_, i) =>
    dayjs(nextWeek).add(i, "day").toDate(),
  );
  const allDays = [...currentWeekDays, ...nextWeekDays];

  const parseTimeToMinutes = (timeValue: string) => {
    const match = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const period = match[3].toUpperCase();
    const normalizedHours = (hours % 12) + (period === "PM" ? 12 : 0);
    return normalizedHours * 60 + minutes;
  };

  const getDurationMinutes = (timeRange: string) => {
    const parts = timeRange.split(" - ");
    if (parts.length !== 2) return 0;
    const start = parseTimeToMinutes(parts[0]);
    const end = parseTimeToMinutes(parts[1]);
    if (start === null || end === null) return 0;
    const adjustedEnd = end < start ? end + 24 * 60 : end;
    return Math.max(0, adjustedEnd - start);
  };

  const totalHoursByEmployee = useMemo(() => {
    const totals = new Map<string, number>();
    scheduleData.forEach((schedule) => {
      const totalMinutes = schedule.days
        .flat()
        .reduce((sum, day) => sum + getDurationMinutes(day.time), 0);
      totals.set(schedule.key.toString(), totalMinutes / 60);
    });
    return totals;
  }, [scheduleData]);

  const handleEmptySlotClick = (employee: Employee, date: Date) => {
    // Convert to your event format for the add modal
    const eventData = {
      employeeId: employee.id,
      title: "New Event",
      description: "New schedule event",
      startTime: "09:00",
      endTime: "17:00",
      category: "scheduled",
      date: dayjs(date).format("YYYY-MM-DD"),
    };
    onAddEvent(eventData);
  };

  const handleEventClick = (event: Day) => {
    // Extract schedule ID from the event key
    setSelectedScheduleId(Number(event.key));
    setIsDrawerOpen(true);
  };

  // Get schedule data for a specific employee and day
  const getEmployeeScheduleForDay = (
    employeeId: string,
    day: Date,
  ): Day[] => {
    const employeeSchedule = scheduleData.find(
      (schedule) => schedule.key.toString() === employeeId,
    );
    if (!employeeSchedule) {
      return [];
    }

    const dateKey = dayjs(day).format("YYYY-MM-DD");

    const matchedByDate = employeeSchedule.days
      .flat()
      .filter((entry) => entry.workDate === dateKey);

    if (matchedByDate.length > 0) {
      return matchedByDate;
    }

    return employeeSchedule.days[day.getDay()] || [];
  };

  const renderWeekColumn = (days: Date[]) => {
    return days.map((day) => {
      const isCurrentDay = dayjs(day).isSame(dayjs(), "day");
      const dateKey = dayjs(day).format("YYYY-MM-DD");
      const columnWidth = getColumnWidth();

      return {
        title: (
          <div
            style={{
              textAlign: "center",
              padding: "8px 4px",
              backgroundColor: isCurrentDay
                ? token.colorPrimaryBg
                : "transparent",
            }}
          >
            <div
              style={{
                color: isCurrentDay ? token.colorPrimary : token.colorText,
                fontWeight: isCurrentDay ? 600 : "normal",
                fontSize: 12,
              }}
            >
              {dayjs(day).format("ddd")}
            </div>
            <div
              style={{
                color: isCurrentDay ? token.colorPrimary : token.colorText,
                fontWeight: isCurrentDay ? 600 : "normal",
                fontSize: 14,
              }}
            >
              {dayjs(day).format("D")}
            </div>
            {isCurrentDay && (
              <div
                style={{
                  width: 20,
                  height: 2,
                  backgroundColor: token.colorPrimary,
                  margin: "4px auto 0",
                }}
              />
            )}
          </div>
        ),
        dataIndex: dateKey,
        key: dateKey,
        width: columnWidth,
        render: (_: string, record: EmployeeRecord) => {
          const dayEvents = getEmployeeScheduleForDay(record.employee.id, day);
          const slotKey = `${record.employee.id}-${dateKey}`;
          const isHovered = hoveredSlot === slotKey;

          return (
            <div
              style={{
                minHeight: 100,
                padding: 6,
                position: "relative",
                cursor: dayEvents.length === 0 ? "pointer" : "default",
                backgroundColor: isCurrentDay
                  ? token.colorPrimaryBg
                  : "transparent",
                transition: "all 0.2s ease",
                borderRadius: 4,
              }}
              onMouseEnter={() => {
                if (dayEvents.length === 0) {
                  setHoveredSlot(slotKey);
                }
              }}
              onMouseLeave={() => setHoveredSlot(null)}
              onClick={() => {
                if (dayEvents.length === 0) {
                  handleEmptySlotClick(record.employee, day);
                }
              }}
            >
              {dayEvents.length === 0 ? (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <PlusOutlined
                      style={{
                        fontSize: 20,
                        color: token.colorPrimary,
                        border: `2px dashed ${token.colorPrimary}`,
                        borderRadius: "50%",
                        padding: 8,
                        backgroundColor: token.colorPrimaryBg,
                      }}
                    />
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 10,
                        color: token.colorPrimary,
                        fontWeight: 500,
                      }}
                    >
                      Add
                    </div>
                  </div>
                </div>
              ) : (
                <Space direction="vertical" size={3} style={{ width: "100%" }}>
                  {dayEvents.map((event) => {
                    const isAbsent = event.isAbsent === true;
                    const backgroundColor = isAbsent
                      ? token.colorWarningBg
                      : event.color || token.colorFillSecondary;
                    const textColor = isAbsent
                      ? token.colorWarningText
                      : getOptimalTextColor(backgroundColor);
                    const isUnpublished = event.isPublished === false;

                    return (
                      <div
                        key={event.key}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event);
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "6px 8px",
                          borderRadius: 4,
                          backgroundColor,
                          border: `1px solid ${
                            isAbsent
                              ? token.colorWarningBorder
                              : backgroundColor
                          }`,
                          borderStyle: isUnpublished ? "dashed" : "solid",
                          opacity: isUnpublished ? 0.8 : 1,
                          transition: "all 0.2s ease",
                          position: "relative",
                          minHeight: 50,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = `0 2px 8px ${token.colorPrimaryBg}`;
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {isUnpublished && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginBottom: 3,
                            }}
                          >
                            <Tooltip title="Schedule not published">
                              <div
                                style={{
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  borderRadius: "50%",
                                  padding: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                }}
                              >
                                <EyeInvisibleOutlined
                                  style={{
                                    fontSize: 8,
                                    color: token.colorError,
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </div>
                        )}

                        <div style={{ marginBottom: 3 }}>
                          <Tag
                            color={
                              categoryColors[event.tag.toLowerCase()] ||
                              "default"
                            }
                            style={{
                              margin: 0,
                              fontSize: 8,
                              lineHeight: 1,
                              padding: "1px 4px",
                              border: "none",
                              borderRadius: 2,
                              fontWeight: 500,
                              display: "inline-block",
                              maxWidth: "100%",
                            }}
                          >
                            {event.tag.charAt(0).toUpperCase()}
                          </Tag>
                        </div>

                        <div
                          style={{
                            fontSize: 9,
                            color: textColor,
                            opacity: 0.9,
                            fontWeight: 500,
                          }}
                        >
                          {event.time.split(" - ")[0]}
                        </div>

                        {isUnpublished && (
                          <div
                            style={{
                              fontSize: 7,
                              color: textColor,
                              opacity: 0.8,
                              marginTop: 2,
                              fontStyle: "italic",
                              fontWeight: 500,
                            }}
                          >
                            Draft
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Space>
              )}
            </div>
          );
        },
      };
    });
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee",
      key: "employee",
      fixed: "left" as const,
      width: getEmployeeColumnWidth(),
      render: (employee: Employee) => {
        const totalHours = totalHoursByEmployee.get(employee.id) ?? 0;
        return (
          <Space size="small">
            <Avatar src={employee.avatar} size="small">
              {employee.firstName?.[0] || employee.name[0]}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: 12, color: token.colorText }}>
                {employee.name}
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {employee.role}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Total: {totalHours.toFixed(2)}h
                </Text>
              </div>
            </div>
          </Space>
        );
      },
    },
    ...renderWeekColumn(currentWeekDays),
    ...renderWeekColumn(nextWeekDays),
  ];

  const dataSource = employees.map((employee) => ({
    key: employee.id,
    employee,
  }));

  // Find today's column index for continuous highlighting
  const todayColumnIndex = allDays.findIndex((day) =>
    dayjs(day).isSame(dayjs(), "day"),
  );

  return (
    <>
      <style>
        {`
          ${
            todayColumnIndex >= 0
              ? `
            .ant-table-thead > tr > th:nth-child(${todayColumnIndex + 2}),
            .ant-table-tbody > tr > td:nth-child(${todayColumnIndex + 2}) {
              background-color: ${token.colorPrimaryBg} !important;
            }
            
            .ant-table-thead > tr > th:nth-child(${todayColumnIndex + 2}) {
              background-color: ${token.colorPrimaryBg} !important;
            }
          `
              : ""
          }
        `}
      </style>

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {/* Week Headers */}
        <Row
          gutter={0}
          style={{
            backgroundColor: token.colorFillAlter,
            padding: "12px 0",
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Col style={{ width: getEmployeeColumnWidth() }} />
          <Col flex="1">
            <Row gutter={0}>
              <Col span={12}>
                <Card
                  size="small"
                  style={{
                    textAlign: "center",
                    margin: "0 4px",
                    backgroundColor: token.colorPrimaryBg,
                    borderColor: token.colorPrimary,
                  }}
                >
                  <Title
                    level={5}
                    style={{ margin: 0, color: token.colorPrimary }}
                  >
                    Current Week
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(currentWeek).format("MMM D")} -{" "}
                    {dayjs(currentWeek).add(6, "day").format("MMM D, YYYY")}
                  </Text>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  size="small"
                  style={{
                    textAlign: "center",
                    margin: "0 4px",
                    backgroundColor: token.colorSuccessBg,
                    borderColor: token.colorSuccess,
                  }}
                >
                  <Title
                    level={5}
                    style={{ margin: 0, color: token.colorSuccess }}
                  >
                    Next Week
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(nextWeek).format("MMM D")} -{" "}
                    {dayjs(nextWeek).add(6, "day").format("MMM D, YYYY")}
                  </Text>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Schedule Grid */}
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          scroll={{
            x:
              screenWidth < 768
                ? getEmployeeColumnWidth() + getColumnWidth() * 14
                : "max-content",
          }}
          bordered
          size="small"
          loading={loading}
          style={{ backgroundColor: token.colorBgContainer }}
        />
      </Space>

      {selectedScheduleId && (
        <ScheduleDetailsDrawer
          scheduleId={selectedScheduleId}
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedScheduleId(null);
          }}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

export default TwoWeekViewGrid;
